import React from 'react';
import { Check, XCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { BOUTON_PIED_CLASSES, MODAL_CONTENT_CLASSES } from '../nosDepensesUtils';

/**
 * Décision sur une demande de dotation.
 *
 * Monté sur les primitives `Dialog`, comme l'exige DESIGN-SYSTEM.md. Il posait
 * auparavant son propre voile via `MODAL_OVERLAY_Z40_CLASS` — une constante
 * réservée au hors-Radix — sans `createPortal` : dès qu'un `Dialog` s'ouvrait
 * ailleurs, le `pointer-events: none` que Radix pose sur le `body` rendait ce
 * modal inerte.
 *
 * Remplace le `window.prompt()` d'origine, même endpoint (deciderDemandeDotation).
 */
const DecisionDotationModal = ({
  isOpen,
  onClose,
  demande,
  decision,
  comment,
  setComment,
  isProcessing,
  onSubmit
}) => {
  const isRefus = decision === 'refusee';

  const demandeur = [demande?.demandeur_prenom, demande?.demandeur_nom].filter(Boolean).join(' ');
  const beneficiaire = [demande?.beneficiaire_prenom, demande?.beneficiaire_nom].filter(Boolean).join(' ');

  return (
    <Dialog open={Boolean(isOpen && demande)} onOpenChange={(ouvert) => !ouvert && onClose?.()}>
      <DialogContent className={`${MODAL_CONTENT_CLASSES} max-w-2xl`}>
        <DialogHeader className="flex-row items-center gap-3 space-y-0 border-b px-5 py-4">
          <div
            className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg text-white ${isRefus ? 'bg-red-600' : 'bg-[#246990]'
              }`}
          >
            {isRefus ? <XCircle size={18} /> : <Check size={18} />}
          </div>
          <div className="min-w-0 flex-1 text-left">
            <DialogTitle className="text-lg font-semibold text-gray-900">
              {isRefus ? 'Refuser la demande de dotation' : 'Valider la demande de dotation'}
            </DialogTitle>
            <DialogDescription className="text-sm text-gray-600">
              {isRefus
                ? 'Le motif est transmis au demandeur.'
                : 'La dotation sera portée au portefeuille du bénéficiaire.'}
            </DialogDescription>
          </div>
        </DialogHeader>

        <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
          <div className="rounded-lg border border-[#246990]/20 bg-[#eef7fb] p-3 text-sm text-gray-700">
            <p>
              <strong>{demandeur || 'Demandeur inconnu'}</strong> vers{' '}
              <strong>{beneficiaire || 'bénéficiaire inconnu'}</strong>
            </p>
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">
              {isRefus ? (
                <>Motif du refus <span className="text-red-500">*</span></>
              ) : (
                'Commentaire de validation (optionnel)'
              )}
            </label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={4}
              placeholder={isRefus
                ? 'Expliquez pourquoi cette demande est refusée…'
                : 'Ajoutez un commentaire si nécessaire…'}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-[#246990]"
            />
          </div>
        </div>

        <DialogFooter className="border-t bg-gray-50 px-5 py-4">
          <Button
            variant="outline"
            className={BOUTON_PIED_CLASSES}
            onClick={onClose}
            disabled={isProcessing}
          >
            Annuler
          </Button>
          <Button
            className={`${BOUTON_PIED_CLASSES} text-white ${isRefus
              ? 'bg-red-600 hover:bg-red-700'
              : 'bg-[#246990] hover:bg-[#1f5a7a]'
              }`}
            disabled={isProcessing}
            onClick={onSubmit}
          >
            {isProcessing ? 'Traitement…' : isRefus ? 'Confirmer le refus' : 'Confirmer la validation'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DecisionDotationModal;
